import { Button, Input, Label, Spinner } from '@1trading/react';
import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { PayoutAccountsPanel } from '@/features/payout-accounts/payout-accounts-panel';
import { usePayoutAccounts } from '@/hooks/queries/funds/use-payout-accounts';
import { daysAgo, toDateInputValue } from '@/utils/date';

const DEFAULT_RANGE_DAYS = 90;
const NOON_HOUR = 12;

interface DateRangeDraft {
  from: string;
  to: string;
}

// (fix): legacy initialised toDate as a raw epoch number and only survived
// because the picker re-emitted a Date during setup — start from real dates here.
const defaultRange = (): DateRangeDraft => ({
  from: toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS)),
  to: toDateInputValue(new Date()),
});

// (know): the legacy picker pinned selections to 12:00 LOCAL before emitting;
// combined with bop-api's UTC day-rounding that preserves the picked calendar day for
// European timezones — keep the same normalisation.
const toNoonLocal = (value: string): Date => {
  const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
  return new Date(year, month - 1, day, NOON_HOUR, 0, 0, 0);
};

const PayoutAccountsPage = () => {
  const [draft, setDraft] = useState<DateRangeDraft>(defaultRange);
  // Fetches once on mount with the 90-day default; afterwards only the Update button
  // applies the draft, so edits to the pickers never fetch on their own.
  const [submitted, setSubmitted] = useState<DateRangeDraft>(draft);

  const range = useMemo(() => ({ fromDate: toNoonLocal(submitted.from), toDate: toNoonLocal(submitted.to) }), [submitted]);

  // Same query the panel renders (shared key) — exposes the refetch state for the
  // Update button's own spinner (refetch never blanks the page).
  const { isFetching, refetch } = usePayoutAccounts(range);

  // (preserve): bop-api ignores a lone date — require both before applying,
  // so clearing just one can never reach the wire.
  const isRangeComplete = draft.from !== '' && draft.to !== '';

  const handleUpdate = () => {
    if (!isRangeComplete) return;
    if (draft.from === submitted.from && draft.to === submitted.to) {
      refetch();
      return;
    }
    setSubmitted(draft);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader documentTitle="Payout Accounts - Bespoke Ops Portal - One Trading" title="Payout Accounts" />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="payout-date-from">Date From</Label>
          <Input className="w-44" id="payout-date-from" onChange={(event) => setDraft((previous) => ({ ...previous, from: event.target.value }))} type="date" value={draft.from} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="payout-date-to">Date To</Label>
          <Input className="w-44" id="payout-date-to" onChange={(event) => setDraft((previous) => ({ ...previous, to: event.target.value }))} type="date" value={draft.to} />
        </div>
        <Button aria-label="Update" disabled={!isRangeComplete || isFetching} onClick={handleUpdate} size="icon" type="button">
          {isFetching ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
        </Button>
        {isRangeComplete ? null : <p className="text-sm text-red-500">Select both dates — the range only applies when both are set.</p>}
      </div>

      <PayoutAccountsPanel fromDate={range.fromDate} toDate={range.toDate} />
    </div>
  );
};
PayoutAccountsPage.displayName = 'PayoutAccountsPage';

export default PayoutAccountsPage;
